// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { ChargeItem, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useState } from 'react';

export interface UsePatientChargeItemsResult {
  chargeItems: ChargeItem[];
  loading: boolean;
  error: Error | null;
  refresh: () => void;
}

/**
 * Loads all ChargeItems for a patient, sorted by most recent first. Used by
 * the Profile tab's History of Charges table.
 */
export function usePatientChargeItems(patient: Patient | undefined): UsePatientChargeItemsResult {
  const medplum = useMedplum();
  const [chargeItems, setChargeItems] = useState<ChargeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (!patient?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const results = await medplum.searchResources('ChargeItem', {
        subject: `Patient/${patient.id}`,
        _sort: '-_lastUpdated',
        _count: '100',
      });
      setChargeItems(results);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load charge items'));
    } finally {
      setLoading(false);
    }
  }, [medplum, patient?.id]);

  useEffect(() => {
    fetch().catch(console.error);
  }, [fetch]);

  return { chargeItems, loading, error, refresh: fetch };
}
